import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { GripVertical, X } from 'lucide-react';
import { Panel, PanelResizeHandle } from 'react-resizable-panels';

const normalizePosition = (value) => (value === 'left' ? 'left' : 'right');
const normalizeVariant = (value) => (value === 'fill' ? 'fill' : 'dock');
const RESIZE_LIMIT_PERCENT = 50;
const MIN_PANEL_PERCENT = 100 / 3;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Panel-only side pane for use inside a parent PanelGroup.
 * The app controls main content and layout; this component controls only the side panel.
 *
 * @param {'dock'|'fill'} [props.variant='dock'] — `dock` wraps content in `Panel` + optional resize handle.
 *   `fill` renders the same chrome/body as a plain flex column (use when the host gives the panel the full row, e.g. app-level “fullscreen” chat).
 */
const ResizableSidePanel = ({
  isOpen = false,
  onClose,
  children,
  position = 'right',
  variant = 'dock',
  order,
  defaultSize = 32,
  minSize = 20,
  maxSize = 50,
  fullMode = false,
  closeOnEscape = true,
  showResizeHandle = true,
  panelClassName = '',
  panelBodyClassName = '',
  resizeHandleClassName = '',
  lightBgClassName = 'bg-white',
  darkBgClassName = 'dark:bg-gray-900',
  topNavLeftContent,
  topNavLeft,
  topNavActionsContent,
  /** Optional second row inside the header (e.g. in-conversation search strip). */
  topNavBelowRow = null,
  hideCloseState = false,
  topNavCloseAriaLabel = 'Close panel',
  topNavClassName = '',
  topNavLeftClassName = '',
  topNavActionsClassName = '',
  panelAriaLabel = 'Resizable side panel',
}) => {
  const side = normalizePosition(position);
  const layoutVariant = normalizeVariant(variant);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const resolvedTopNavLeftContent = topNavLeftContent ?? topNavLeft;
  const showCloseAction = Boolean(hideCloseState);
  const showHandle = showResizeHandle && !fullMode && layoutVariant === 'dock';

  const effectiveMaxSize = useMemo(
    () => (fullMode ? 100 : clamp(maxSize, MIN_PANEL_PERCENT, RESIZE_LIMIT_PERCENT)),
    [fullMode, maxSize]
  );
  const effectiveMinSize = useMemo(
    () => (fullMode ? 100 : clamp(minSize, MIN_PANEL_PERCENT, effectiveMaxSize)),
    [fullMode, minSize, effectiveMaxSize]
  );
  const effectiveDefaultSize = useMemo(
    () => (fullMode ? 100 : clamp(defaultSize, effectiveMinSize, effectiveMaxSize)),
    [fullMode, defaultSize, effectiveMinSize, effectiveMaxSize]
  );
  const hasTopNav =
    Boolean(resolvedTopNavLeftContent) ||
    Boolean(topNavActionsContent) ||
    showCloseAction ||
    Boolean(topNavBelowRow);

  const panelClassNames = useMemo(
    () =>
      [
        layoutVariant === 'fill'
          ? 'flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-edge-strong'
          : 'h-full w-full overflow-hidden border-edge-strong',
        side === 'left' ? 'border-r' : 'border-l',
        lightBgClassName,
        darkBgClassName,
        panelClassName,
      ]
        .filter(Boolean)
        .join(' '),
    [layoutVariant, side, lightBgClassName, darkBgClassName, panelClassName]
  );
  const topNavClassNames = useMemo(
    () => ['shrink-0 border-b border-edge bg-surface px-4 py-3', topNavClassName].filter(Boolean).join(' '),
    [topNavClassName]
  );
  const topNavLeftClassNames = useMemo(
    () => ['min-w-0', topNavLeftClassName].filter(Boolean).join(' '),
    [topNavLeftClassName]
  );
  const topNavActionsClassNames = useMemo(
    () => ['flex shrink-0 items-center gap-1 rounded-lg bg-surface p-1', topNavActionsClassName].filter(Boolean).join(' '),
    [topNavActionsClassName]
  );
  const panelBodyClassNames = useMemo(
    () => ['min-h-0 flex-1 overflow-hidden', panelBodyClassName].filter(Boolean).join(' '),
    [panelBodyClassName]
  );
  const resizeHandleClassNames = useMemo(
    () =>
      [
        'relative w-1 cursor-col-resize bg-gray-200 transition-colors',
        'dark:bg-gray-700 hover:bg-blue-500 dark:hover:bg-blue-600',
        'data-[resize-handle-active]:bg-blue-500 dark:data-[resize-handle-active]:bg-blue-600',
        'group',
        resizeHandleClassName,
      ]
        .filter(Boolean)
        .join(' '),
    [resizeHandleClassName]
  );

  const handleClose = useCallback(() => {
    onCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, handleClose]);

  if (!isOpen) return null;

  const panelInner = (
    <aside
      aria-label={panelAriaLabel}
      className={panelClassNames}
    >
      <div className="flex h-full min-h-0 flex-col">
        {hasTopNav ? (
          <header className={topNavClassNames}>
            <div className="flex items-center justify-between gap-3">
              <div className={topNavLeftClassNames}>{resolvedTopNavLeftContent}</div>
              {topNavActionsContent || showCloseAction ? (
                <div className={topNavActionsClassNames}>
                  {topNavActionsContent}

                  {showCloseAction ? (
                    <button
                      type="button"
                      aria-label={topNavCloseAriaLabel}
                      title={topNavCloseAriaLabel}
                      onClick={handleClose}
                      className={[
                        'inline-flex h-8 w-8 items-center justify-center rounded-md',
                        'text-content-secondary transition-colors',
                        'hover:bg-surface-hover hover:text-content',
                      ].join(' ')}
                    >
                      <X size={16} />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {topNavBelowRow ? (
              <div className="mt-2 min-w-0 border-t border-edge pt-2">{topNavBelowRow}</div>
            ) : null}
          </header>
        ) : null}
        <div className={panelBodyClassNames}>{children}</div>
      </div>
    </aside>
  );

  if (layoutVariant === 'fill') {
    return panelInner;
  }

  const sidePanel = (
    <Panel
      key="side"
      defaultSize={effectiveDefaultSize}
      minSize={effectiveMinSize}
      maxSize={effectiveMaxSize}
      order={order}
    >
      {panelInner}
    </Panel>
  );

  const handle = showHandle ? (
    <PanelResizeHandle
      className={resizeHandleClassNames}
    >
      <div className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center justify-center bg-[#00000000]">
        <div
          className={[
            'rounded-full bg-gray-300 p-1 opacity-0 transition-opacity',
            'dark:bg-gray-600 group-hover:opacity-100',
            'group-hover:bg-blue-500 dark:group-hover:bg-blue-600',
            'group-data-[resize-handle-active]:opacity-100',
            'group-data-[resize-handle-active]:bg-blue-500 dark:group-data-[resize-handle-active]:bg-blue-600',
          ].join(' ')}
        >
          <GripVertical className="size-3 text-white" />
        </div>
      </div>
    </PanelResizeHandle>
  ) : null;

  if (!showHandle) return sidePanel;

  return side === 'left' ? (
    <>
      {sidePanel}
      {handle}
    </>
  ) : (
    <>
      {handle}
      {sidePanel}
    </>
  );
};

export default memo(ResizableSidePanel);
